import Card from "../../components/Card/card";
import { useEffect, useMemo, useState, useContext } from "react";
import "./charts-tab.css";
import { WIDGET_NAMES } from "../../strings";
import Energy_generation from "../../components/Microgrid-Opt-Components/Dashboard-cmpts/Energy-Generation/Energy_generation";
import WaterConsumption from "../../components/Microgrid-Opt-Components/Dashboard-cmpts/Water-consumption/Water-consumption";
import GasConsumption from "../../components/Microgrid-Opt-Components/Dashboard-cmpts/Gas-consumption/Gas-consumption";
import EnergyConsumption from "../../components/Microgrid-Opt-Components/Dashboard-cmpts/Energy-consumption/Energy-consumption";
import { useQuery } from "@tanstack/react-query";
import chartConfig from "./Energy-charts.json";
import { RootContext } from "../../contexts/root-context";
import { ERROR_LIST } from "../../config/errors";
import { Spinner } from "@abb/abb-common-ux-react";
import { BASE_URL } from "../../strings";
import { RATINGEN_MEASURES } from "../../features/ratingen/payloads/payload";

const headers = { 'Content-Type': 'application/json' };

const nodes = [
    { name: 'PV1' },
    { name: 'PV2' },
    { name: 'BHKW Generator 1' },
    { name: 'BHKW Generator 2' },
    { name: 'Generation' },
    { name: 'Fabrik' },
    { name: 'Netzbezug' },
    { name: 'Einspeisung' },
]

const energyFormatter = (value) => {
    return Number.parseFloat((value / 1000).toFixed(2)); // Convert to thousands and format to 2 decimal places
}

const CardMessage = ({ message }) => (
    <div className="card-center-message">
        <span>{message}</span>
    </div>
);

const CardLoading = () => (
    <div className="card-center-message">
        <Spinner color="dark-grey" size="small" />
    </div>
);

const isZeroDataset = (dataset) => {
    if (!dataset || typeof dataset !== "object") {
        return true;
    }

    const values = Object.values(dataset).flatMap((series) => (Array.isArray(series) ? series : []));
    const numericValues = values
        .map((item) => {
            if (item && typeof item === "object" && "value" in item) {
                return Number(item.value);
            }
            return Number(item);
        })
        .filter((value) => !Number.isNaN(value));

    if (numericValues.length === 0) {
        return true;
    }

    return numericValues.every((value) => value === 0);
};

function MicrogridChartsTab({ data }) {
    const { ratingenTimePeriod } = useContext(RootContext);
    const [fetchLoads, setfetchLoads] = useState([]);
    const [chartData, setChartData] = useState(chartConfig.sampleData || []);
    const sankeyData = useMemo(() => {
        const pv1 = energyFormatter(data?.Verwaltungsgebaude_UG_Station4_PV_1951063_Energie ?? 0);
        const pv2 = energyFormatter(data?.Empfang_PV_Anlage_UV_rechts_oben_1395203 ?? 0);
        const bhk1 = energyFormatter(data?.BHKW_Generator1_71282_Energie ?? 0);
        const bhk2 = energyFormatter(data?.BHKW_Generator2_71284_Energie ?? 0);
        const netzbezug = energyFormatter(data?.BHKW_Gesamtbezug_ABB_Wirk_Plus_1_Energie ?? 0);
        const einspeisung = energyFormatter(data?.BHKW_Gesamteinspeisung_ABB_Wirk_Minus_2_Energie ?? 0);
        const fabrik = Number.parseFloat((pv1 + pv2 + bhk1 + bhk2 + netzbezug).toFixed(2));
        const generation = Number.parseFloat((bhk1 + bhk2 -einspeisung).toFixed(2));
        return {
            nodes: nodes,
            links: [
                { source: 'PV1', target: 'Fabrik', value: pv1 },
                { source: 'PV2', target: 'Fabrik', value: pv2 },
                { source: 'BHKW Generator 1', target: 'Generation', value: bhk1 },
                { source: 'BHKW Generator 2', target: 'Generation', value: bhk2 },
                { source: 'Netzbezug', target: 'Fabrik', value: netzbezug },

                { source: 'Generation', target: 'Fabrik', value: generation },
                { source: 'Generation', target: 'Einspeisung', value: einspeisung },
            ],
        }
    }, [data, ratingenTimePeriod]);


    const { data: summaryData, isSuccess: chartSuccess, error: chartError, isLoading: chartLoading } = useQuery({
        queryKey: ['ratingenGraph', ratingenTimePeriod],
        retry: 5,
        queryFn: async () => {
            console.log('Fetching chart data...');
            const response = await fetch(`${BASE_URL}/trend-graph?` + new URLSearchParams({
                site: 'ratingen',
                time_period: ratingenTimePeriod,
                data_type: 'graph',
                measure: RATINGEN_MEASURES.join(",")
            }), {
                method: 'POST',
                headers: headers,
            });

            if (!response.ok) {
               
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Chart data received'+data);
           
            return data;
        }
    })

    useEffect(() => {
        if (chartSuccess && Object.keys(summaryData).length > 0) {
            setChartData(summaryData);
        } else if (chartError) {
            console.error('Chart data fetch error:', chartError);
        } else {
            setChartData(chartConfig.sampleData || []);
        }
    }, [chartSuccess, summaryData, chartError, ratingenTimePeriod]);

    // Debug log for chartData changes
    useEffect(() => {
        console.log('chartData state updated:', chartData);
    }, [chartData, ratingenTimePeriod]);

    const isChartEmpty = useMemo(() => isZeroDataset(chartData), [chartData]);
    const isSankeyEmpty = useMemo(() => {
        if (!sankeyData || !Array.isArray(sankeyData.links)) {
            return true;
        }
        return sankeyData.links.every((link) => Number(link.value) === 0);
    }, [sankeyData]);

    // Example usage of Card with customizable props
    return (
        <div className="container-fluid dashboard-container">
            <div className="row dashboard-main-row d-flex">
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 dashboard-left-section d-flex">
                    <Card
                        name={'DefaultCard'}
                        title={WIDGET_NAMES.find(w => w.key === "energy_generation")?.label || 'Energy Generation (MWh)'}
                        size="large"
                        className={"large-card-style w-100"}
                        scroll={true}
                        visible={true}
                    >
                        {isSankeyEmpty ? (
                            <CardMessage message={ERROR_LIST.NO_DATA} />
                        ) : (
                            <Energy_generation data={sankeyData} />
                        )}
                    </Card>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 dashboard-right-section d-flex flex-column">
                    <div className="dashboard-top-row flex-fill">
                        <div className="col-12 d-flex">
                            <Card
                                name={fetchLoads[0] || 'DefaultCard'}
                                title={WIDGET_NAMES.find(w => w.key === "energy_consumption")?.label || 'Energy Consumption'}
                                size="medium"
                                className={"medium-card-style w-100"}
                                scroll={true}
                                visible={true}
                            >
                                {chartLoading ? (
                                    <CardLoading />
                                ) : chartError ? (
                                    <CardMessage message={ERROR_LIST.ERROR_FETCH_DATA} />
                                ) : isChartEmpty ? (
                                    <CardMessage message={ERROR_LIST.NO_DATA} />
                                ) : (
                                    <EnergyConsumption data={chartData} />
                                )}
                            </Card>
                        </div>
                    </div>
                    <div className="dashboard-bottom-row flex-fill">
                        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12 d-flex">
                            <Card
                                name={fetchLoads[0] || 'DefaultCard'}
                                title={WIDGET_NAMES.find(w => w.key === "water_consumption")?.label || 'Water Consumption'}
                                size="small"
                                className={"small-card-style w-100"}
                                scroll={true}
                                visible={true}
                            >
                                {chartLoading ? (
                                    <CardLoading />
                                ) : chartError ? (
                                    <CardMessage message={ERROR_LIST.ERROR_FETCH_DATA} />
                                ) : isChartEmpty ? (
                                    <CardMessage message={ERROR_LIST.NO_DATA} />
                                ) : (
                                    <WaterConsumption data={chartData} />
                                )}
                            </Card>
                        </div>
                        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12 d-flex">
                            <Card
                                name={fetchLoads[0] || 'DefaultCard'}
                                title={WIDGET_NAMES.find(w => w.key === "gas_consumption")?.label || 'Gas Consumption'}
                                size="small"
                                className={"small-card-style w-100"}
                                scroll={true}
                                visible={true}
                            >
                                {chartLoading ? (
                                    <CardLoading />
                                ) : chartError ? (
                                    <CardMessage message={ERROR_LIST.ERROR_FETCH_DATA} />
                                ) : isChartEmpty ? (
                                    <CardMessage message={ERROR_LIST.NO_DATA} />
                                ) : (
                                    <GasConsumption data={chartData} />
                                )}
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MicrogridChartsTab;
